import * as Popover from '@radix-ui/react-popover'
import { CalendarViewContextState } from '../../context'
import { BaseWorkerCardCell } from './BaseWorkerCardCell'
import { WorkerCardCellDetail } from './WorkerCardCellDetail'
import { useWorkerProfile } from 'queries/worker/useWorkerProfile'
import { Info } from 'lucide-react'
import { Tooltip, useAppTheme, Text } from '@workwhile/ui'
import { useAuthContext } from 'components/auth'

type WorkerCardProps = {
  worker: CalendarViewContextState['state']['workers'][number]
}

export const WorkerCardCell = ({ worker }: WorkerCardProps) => {
  // Fetch company-wide stats
  const { data: profileData, isLoading } = useWorkerProfile(worker.id)
  const { company: companyInfo } = useAuthContext()
  const theme = useAppTheme()

  if (isLoading || !profileData) {
    return (
      <BaseWorkerCardCell
        title={
          <Text fontSize={0} color="lightText" textAlign="left">
            Loading worker stats ...
          </Text>
        }
      />
    )
  }

  const numHoursForCompany = profileData.worker.numHoursForCompany ?? 0
  const companyName = companyInfo?.name ?? 'the company'

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <BaseWorkerCardCell
          avatar={{
            photoUrl: worker.profilePicUrl,
            name: worker.name,
          }}
          title={
            <div>
              <Text fontSize={1} textAlign="left" fontWeight="500">
                {worker.name}
              </Text>
              <Text fontSize={0} color="lightText" textAlign="left">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 1,
                  }}
                >
                  {numHoursForCompany} hours
                  <Tooltip
                    content={`Number of hours ${worker.name} has worked for ${companyName}`}
                    side="bottom"
                    align="start"
                    sideOffset={2}
                    variant="neutral"
                    style={{
                      maxWidth: '200px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <Info
                        size={12}
                        color={theme.colors.neutrals[300]}
                        aria-label="Info"
                      />
                    </span>
                  </Tooltip>
                </span>
              </Text>
            </div>
          }
        />
      </Popover.Trigger>
      <Popover.Content
        side="right"
        align="start"
        sideOffset={5}
        asChild
        style={{ zIndex: 1 }}
      >
        <WorkerCardCellDetail worker={worker} />
      </Popover.Content>
    </Popover.Root>
  )
}
